import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@mui/material";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useNavigate,
} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "quill/dist/quill.core.css";
import "quill/dist/quill.bubble.css";
import "quill/dist/quill.snow.css";

// Local imports
// import CategoriesContainer from "./modules/PromoItems/containers/categories.container";
import { Layout } from "./modules/Layout/containers/layout.container";
import CreatePromoItemContainer from "./modules/PromoItems/containers/create.container";
import ImportPromoItemsContainer from "./modules/PromoItems/containers/import.container";
import ListContainer from "./modules/PromoItems/containers/list.container";
import { muiTheme } from "./app.theme";
import "./App.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import LandingView from "modules/Layout/views/landing.view";
import SuppliersListContainer from "modules/PromoItems/containers/suppliers.container";
import BrowseContainer from "modules/PromoItems/containers/browse.container";
import TasksContainer from "modules/Tasks/containers/tasks.container";
import ErrorBoundary from "modules/UI/components/ErrorBoundary.component";
import { useFeatures } from "modules/FeatureManagement/service/featureManagement.hooks";
import MaintenanceView from "modules/Layout/views/maintenance.view";
import NotFoundView from "modules/Layout/views/notFound.view";
import CampaignsListContainer from "modules/Campaigns/containers/list.container";
import CreateCampaignContainer from "modules/Campaigns/containers/details.container";
import PromoCategoriesContainer from "modules/Admin/containers/PromoCategories.container";
import { IUserRole } from "modules/User/service/user";
import { useActiveUser } from "modules/User/activeUser";
import UnauthorizedView from "modules/Layout/views/unauthorized.view";
import PromoHistoryComponent from "modules/PromoItems/components/PromoHistory.component";
import ExportCatalogsContainer from "modules/PromoItems/containers/export.container";
import TestSecretPage from "modules/Campaigns/containers/TestSecret.container";

const queryClient = new QueryClient();

interface GuardedRouteProps {
  roles: IUserRole[];
  children: React.ReactNode;
}

const GuardedRoute: React.FC<GuardedRouteProps> = ({
  roles = [],
  children,
}) => {
  const activeUser = useActiveUser();
  const role = activeUser.getUserRole();

  if (!roles.includes(role)) {
    return <UnauthorizedView />;
  }
  return <>{children}</>;
};

const router = createBrowserRouter([
  {
    // path: "/",
    element: <Layout />,
    errorElement: <NotFoundView />,
    children: [
      {
        path: "/",
        element: <LandingView />,
      }, 
      {
        path: "/browse",
        element: <BrowseContainer />,
      },
      {
        path: "/tasks",
        element: <TasksContainer />,
      },
      // {
      //   path: "/auth",
      //   element: <CategoriesContainer />,
      // },
      {
        path: "/manage",
        element: (
          <GuardedRoute
            roles={[
              IUserRole.PPA_ABOVE_MARKET,
              IUserRole.ABOVE_MARKET,
              IUserRole.AT_MARKET,
              IUserRole.PPA_AT_MARKET,
              IUserRole.SUPPLIER,
            ]}
          >
            <ListContainer />
          </GuardedRoute>
        ),
      },
      {
        path: "/catalogs/new",
        element: (
          <GuardedRoute
            roles={[
              IUserRole.PPA_ABOVE_MARKET,
              IUserRole.ABOVE_MARKET,
              IUserRole.AT_MARKET,
              IUserRole.PPA_AT_MARKET,
              IUserRole.SUPPLIER,
            ]}
          >
            <CreatePromoItemContainer />
          </GuardedRoute>
        ),
      },
      {
        path: "/catalogs/export/:id",
        element: <ExportCatalogsContainer />,
      },
      {
        path: "/catalogs/:promoId/history",
        element: (
          // <GuardedRoute roles={[IUserRole.PPA_ABOVE_MARKET, IUserRole.ABOVE_MARKET, IUserRole.AT_MARKET, IUserRole.PPA_AT_MARKET, IUserRole.SUPPLIER]}>
          <PromoHistoryComponent />
          // </GuardedRoute>
        ),
      },
      {
        path: "/catalogs/:promoId/history/material/:materialId",
        element: (
          // <GuardedRoute roles={[IUserRole.PPA_ABOVE_MARKET, IUserRole.ABOVE_MARKET, IUserRole.AT_MARKET, IUserRole.PPA_AT_MARKET, IUserRole.SUPPLIER]}>
          <PromoHistoryComponent />
          // </GuardedRoute>
        ),
      },
      {
        path: "/catalogs/reactivate/:promoId",
        element: (
          <GuardedRoute
            roles={[
              IUserRole.PPA_ABOVE_MARKET,
              IUserRole.ABOVE_MARKET,
              IUserRole.AT_MARKET,
              IUserRole.PPA_AT_MARKET,
              IUserRole.SUPPLIER,
            ]}
          >
            <CreatePromoItemContainer />
          </GuardedRoute>
        ),
      },
      {
        path: "/catalogs/:promoId/edit",
        element: (
          <GuardedRoute
            roles={[
              IUserRole.PPA_ABOVE_MARKET,
              IUserRole.ABOVE_MARKET,
              IUserRole.AT_MARKET,
              IUserRole.PPA_AT_MARKET,
              IUserRole.SUPPLIER,
            ]}
          >
            <CreatePromoItemContainer />
          </GuardedRoute>
        ),
      },
      {
        path: "/catalogs/:promoId/view",
        element: <CreatePromoItemContainer />,
      },
      {
        path: "/catalogs/import",
        element: <ImportPromoItemsContainer />,
      },
      {
        path: "/campaigns",
        element: <CampaignsListContainer />,
      },
      {
        path: "/campaigns/create/new",
        element: (
          <GuardedRoute
            roles={[
              IUserRole.PPA_ABOVE_MARKET,
              IUserRole.ABOVE_MARKET,
              IUserRole.SUPPLIER,
            ]}
          >
            <CampaignsListContainer />
          </GuardedRoute>
        ),
      },
      {
        path: "/campaigns/:campaignId",
        element: <CreateCampaignContainer />,
      },
      {
  path: "/__internal/test-image",
  element: <TestSecretPage />,
},
      {
        path: "/admin/taxonomy",
        element: (
          <GuardedRoute roles={[IUserRole.PPA_ABOVE_MARKET]}>
            <PromoCategoriesContainer />
          </GuardedRoute>
        ),
      },
    ],
  },
]);

function App() {
  //testing only 
  const features = useFeatures();
  return features["maintenance-mode"] ? (
    <MaintenanceView />
  ) : (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ToastContainer />
        <RouterProvider router={router} />
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition={"bottom-left"}
        />
      </LocalizationProvider>
    </QueryClientProvider>
  );
}

export default App;
